/**
 * ConfirmDialog — modal Blueprint de confirmación REUTILIZABLE (Sprint 19).
 *
 * Sustituye a window.confirm en flujos destructivos (primer consumidor:
 * SecurityPage — revocar la sesión actual y «Cerrar las demás»). Estética y
 * patrón idénticos a los modales de PipelinePage: overlay bg-black/40,
 * bp-card sin shadow, cierre con Escape y con click fuera.
 *
 * Accesibilidad: role=dialog + aria-modal + aria-labelledby/aria-describedby.
 * El foco inicial va al botón CANCELAR (acción segura): al ser confirmaciones
 * destructivas, un Enter reflejo no debe ejecutar la acción.
 *
 * Testids estables para e2e: `confirm-dialog` (contenedor) y
 * `confirm-dialog-accept` (botón de confirmación).
 */
import { useEffect } from 'react'

interface Props {
  /** Título corto del diálogo (p.ej. «Revocar tu sesión actual»). */
  title: string
  /** Mensaje explicativo de la consecuencia de confirmar. */
  message: string
  /** Etiqueta del botón de confirmación (estilo destructivo rojo). */
  confirmLabel: string
  /** Etiqueta del botón de cancelar. Por defecto «Cancelar». */
  cancelLabel?: string
  /** Deshabilita los botones mientras la acción está en vuelo. */
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancelar',
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  // Cierre con Escape (mismo patrón que los modales de PipelinePage).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
      data-testid="confirm-dialog"
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Click fuera del bp-card cierra (solo el overlay, no burbujeos).
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="bp-card bg-white max-w-md w-full p-5">
        <h2 id="confirm-dialog-title" className="text-base font-medium mb-1">
          {title}
        </h2>
        <p id="confirm-dialog-message" className="text-sm text-ink-900 mb-4">
          {message}
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="bp-btn"
            autoFocus
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid="confirm-dialog-accept"
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy}
            className="bp-btn bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConfirmDialog
